/**
 * Child PIN Service
 * Handles PIN verification and generation via Supabase RPCs
 */

import { supabase, supabaseRpc } from '@/lib/supabase'

// Identifiant stable de l'appareil, pour le plafond d'essais par appareil de
// `verify_child_pin`. Il en recevait un neuf à chaque essai
// (`device-${Date.now()}`), donc chaque essai comptait comme le premier.
// Ce plafond n'est qu'un confort : les plafonds par IP et global, côté
// serveur, sont ceux qu'on ne contourne pas (#403).
// Gardé aussi en mémoire : stable pour la session même si le stockage est
// indisponible.
const DEVICE_ID_KEY = 'harmonia_pin_device_id'
let deviceId: string | null = null

function pinDeviceId(): string {
  if (deviceId) return deviceId
  try {
    deviceId = localStorage.getItem(DEVICE_ID_KEY)
  } catch { /* stockage indisponible */ }
  if (!deviceId) {
    deviceId = `device-${crypto.randomUUID()}`
    try {
      localStorage.setItem(DEVICE_ID_KEY, deviceId)
    } catch { /* stockage indisponible */ }
  }
  return deviceId
}

interface VerifyPinResponse {
  childId: string
  email: string
  password: string
  isNewUser: boolean
}

interface GeneratePinResponse {
  pin: string
}

export const childPinService = {
  /**
   * Verify a 6-digit PIN (called from child device, anon context)
   * Returns email/password credentials for signInWithPassword()
   */
  async verifyPin(pin: string, deviceIdentifier?: string): Promise<VerifyPinResponse> {
    const { data, error } = await supabaseRpc.rpc('verify_child_pin', {
      p_pin: pin,
      p_device_identifier: deviceIdentifier || pinDeviceId()
    })

    if (error) {
      // Le SQL lève 'Too many PIN attempts…' : chercher 'Too many attempts'
      // ne le reconnaissait jamais.
      if (/too many (pin )?attempts/i.test(error.message)) {
        throw new Error('TOO_MANY_ATTEMPTS')
      }
      if (error.message.includes('Invalid PIN')) {
        throw new Error('INVALID_PIN')
      }
      // Le PIN est bon mais le foyer n'a plus Harmonia+ : l'espace de l'enfant
      // fait partie de l'abonnement. Erreur typée pour que l'écran de connexion
      // le dise à l'enfant au lieu d'un « code invalide » qui l'enverrait
      // redemander un code parfaitement valide à ses parents.
      if (error.message.includes('premium_required')) {
        throw new Error('PREMIUM_REQUIRED')
      }
      throw new Error(error.message)
    }

    if (!data || data.length === 0) {
      throw new Error('INVALID_PIN')
    }

    const row = data[0]
    return {
      childId: row.out_child_id,
      email: row.out_email,
      password: row.out_password,
      isNewUser: row.out_is_new_user
    }
  },

  /**
   * Generate a new PIN for a child (called by parent, authenticated)
   */
  async generatePin(childId: string): Promise<string> {
    const { data, error } = await supabaseRpc.rpc('generate_child_pin', {
      p_child_id: childId
    })

    if (error) throw new Error(error.message)
    if (!data || data.length === 0) throw new Error('Failed to generate PIN')

    return data[0].pin
  },

  /**
   * Sign in with credentials returned by verifyPin.
   * For new users: signUp first (creates auth.users via GoTrue API properly),
   * then signIn. For existing users: signInWithPassword directly.
   */
  async signInWithCredentials(email: string, password: string, isNewUser: boolean): Promise<{
    userId: string
    session: any
  }> {
    // Sign out any stale session first
    await supabase.auth.signOut()

    if (isNewUser) {
      // Use signUp via GoTrue API — this creates auth.users properly
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: undefined,
        }
      })

      // signUp may auto-confirm (if email confirmation disabled) and return a session
      if (signUpError) {
        // If user already exists (race condition), fall through to signIn
        if (!signUpError.message.includes('already registered')) {
          throw new Error(signUpError.message)
        }
      } else if (signUpData?.session) {
        return {
          userId: signUpData.user!.id,
          session: signUpData.session
        }
      }
    }

    // Sign in with password (works for both existing users and newly signed-up users)
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (error || !data.user || !data.session) {
      // Un compte enfant n'a ni boîte mail ni lien de confirmation : s'il
      // arrive ici non confirmé, c'est que le trigger `auto_confirm_child_account`
      // n'est pas en place sur cette base. Erreur TYPÉE, parce que le message
      // brut de GoTrue retombait dans le fourre-tout de childAuth.ts et
      // s'affichait à l'enfant en « code invalide » — le seul écran qui ne
      // pouvait pas être vrai, puisque le PIN venait justement d'être validé.
      if (error?.message?.includes('Email not confirmed') || (error as { code?: string })?.code === 'email_not_confirmed') {
        throw new Error('CHILD_ACCOUNT_NOT_CONFIRMED')
      }
      throw new Error(error?.message || 'Failed to sign in')
    }

    return {
      userId: data.user.id,
      session: data.session
    }
  }
}

export type { VerifyPinResponse, GeneratePinResponse }
